import { getFullUrl, getFullUrlWithCurrentSubDomain } from 'hubspot-url-utils';
import { genericClient } from 'usage-tracker-core/client';
import * as browserHelper from 'usage-tracker-core/client/browserHelper';
import { sendEventsViaFetch } from 'usage-tracker-core/client/fetchClient';
import * as srConstants from './constants';
const reportNetworkError = err => {
  return genericClient.reportError(err, {
    fingerprint: ['usage-tracker-js', 'network']
  });
};
export const getSessionReplayConfig = (options = {}, deviceId, sessionId) => {
  // the config endpoint always runs on the app load balancer within the SessionReplay service
  const configOrigin = getFullUrl('app');

  // the intake origin must run on the same subdomain where the app is running, due to CORS.
  const intakeOrigin = getFullUrlWithCurrentSubDomain();

  // the `app` load balancer always requires /api for most of services running against app LB
  const intakePrefix = /hubspot(qa)?\.com$/.test(intakeOrigin) ? '/api' : '';
  const {
    // The sample rate of the session replay events. From 0-1 (1 = 100%, 0 = 0%)
    sampleRate = srConstants.DEFAULT_SAMPLE_RATE,
    // If you want to opt-out programmatically from the session replays
    optOut = false,
    // Which elements to block (remove the elements) for PII reasons
    blockSelectors = [],
    // Which elements to mask with ***** (hide with a placeholder) for PII reasons
    maskSelectors = [],
    // Which elements to unmask (restore original content)
    unmaskSelectors = []
  } = options;
  const sessionReplaySettings = {
    // Do compression on idle Browser periods, to reduce impact on user interaction
    performanceConfig: {
      enabled: true
    },
    // Force to IndexedDB store by default; Allows us to store temporary metadata and let the Service Worker
    // compress the events and send it to IndexedDB instead of using the browser's JavaScript main thread.
    storeType: 'idb',
    // We allow Apps to opt-out of Session Replay within their instances when wrapping
    // on `withSessionReplay` method. (Assuming if they want to opt-out) on specific things
    // such as, QA environment, or specific Portals, etc.
    optOut,
    // If no sample-rate is provided, we default to sampling 1% of the sessions
    // @see https://www.docs.developers.amplitude.com/session-replay/sdks/standalone/#sampling-rate
    // Note: When there is no App-specific Sampling Config, it will fallback to the sampling rate used by the SDK
    // Sampling Rates from the Server will only be provided if there is an App-specific sampling rate.
    sampleRate,
    // The HAmplitude Session ID, for all trackers this will be a 1:1 match
    sessionId,
    // The HAmplitude Session ID, for all trackers this will be a 1:1 match
    deviceId,
    // Allows us to mask specific elements from being sent to Amplitude for PII reasons
    // We by default include a bunch of predefined selectors, but Apps should add extra selectors on their own discretion
    privacyConfig: {
      blockSelector:
      // All the elements to be blocked by default on the SDK-level
      srConstants.DEFAULT_PII_SELECTORS.BLOCK.concat(blockSelectors),
      maskSelector:
      // All the elements to be masked by default on the SDK-level
      srConstants.DEFAULT_PII_SELECTORS.MASK.concat(maskSelectors),
      unmaskSelector:
      // All the elements to be unmasked by default on the SDK-level
      srConstants.DEFAULT_PII_SELECTORS.UNMASK.concat(unmaskSelectors),
      // The Conservative Setting by default will mask all text and all form fields
      // including HTML text, user input, links, etc.
      defaultMaskLevel: 'conservative'
    },
    // Passes our Proxy as the Transport Provider for HubSpot
    /** @todo: should be removed once we have a proper Transport Provider for HubSpot */
    configServerUrl: `${configOrigin}${srConstants.CONFIG_ENDPOINT_URL}`,
    // Passes our Proxy as the Transport Provider for HubSpot
    /** @todo: should be removed once we have a proper Transport Provider for HubSpot */
    trackServerUrl: `${intakeOrigin}${intakePrefix}${srConstants.TRACK_ENDPOINT_URL}`,
    // Custom Transport Provider for HubSpot (NOOP at the moment)
    /** @todo: Amplitude SDK at this moment completely ignores (does not support) the `transportProvider` */
    transportProvider: {
      // Note: At this moment it is unclear wether `serverUrl` is always the `track` server URL
      // or a string that changes depending on the request that we want to run.
      send: (_, {
        events
      }) => {
        const endpoint = `${intakeOrigin}${intakePrefix}${srConstants.TRACK_ENDPOINT_URL}`;
        const sendXhr = () => {
          sendEventsViaFetch(endpoint, {
            data: events
          }).catch(reportNetworkError);
        };
        genericClient.sendBeacon(endpoint, events, sendXhr);

        // Satisfy `amplitude-sdk's` Transport Protocol Types
        return Promise.resolve(null);
      }
    }
  };

  // If the user requests debugMode, which can be useful for test environments, or debugging its behaviour locally
  // or simply forcing Session Replay to run, we enable debug mode on the Session Replay level
  // This can be useful for observing sessions and many other things.
  if (!browserHelper.isDebugEnabled) {
    return sessionReplaySettings;
  }
  return Object.assign({}, sessionReplaySettings, {
    // Enables Debug Mode on Session Replay-level
    debugMode: true,
    // Log Level 4 = Debug Logs
    // See: `@amplitude/session-replay-browser/index.d.ts#LogLevel`
    logLevel: 4,
    // We force everyone to not be opted out when debug is enabled
    optOut: false,
    // We force the sample rate to be 100% when debug is enabled
    sampleRate: 1
  });
};